import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { create } from '@bufbuild/protobuf'
import { TimestampSchema } from '@bufbuild/protobuf/wkt'
import {
  adminGetOrganization,
  adminGetOrganizationStats,
  adminEnableFeature,
  adminDisableFeature,
  adminListFeatureCatalog,
} from '../../../gen/cortado/v1/cortado-CortadoService_connectquery'
import { OrganizationFeatureEnum } from '../../../gen/cortado/v1/settings_pb'
import { availableFeaturesFor, featureLabel, splitFeatureCatalog } from '../../../lib/feature-catalog'
import { StatCard } from '../../../components/stat-card'
import { SupportAccessSection } from '../../../components/support/support-access-section'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table'
import { Badge } from '../../../components/ui/badge'
import { Switch } from '../../../components/ui/switch'
import { Button } from '../../../components/ui/button'
import { Label } from '../../../components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import {
  SortableHeader,
  compareNumbers,
  compareStrings,
  useTableSort,
} from '../../../components/ui/sortable-header'
import { ArrowLeft, ChevronDown, ChevronRight, Users, FileText, Calendar, Receipt, DollarSign, Plus } from 'lucide-react'

type FeatureSortField = 'feature' | 'status' | 'expires'
type OrgUserSortField = 'name' | 'email' | 'title' | 'joined'

export const Route = createFileRoute('/_admin/organizations/$organizationId')({
  component: OrganizationDetailPage,
})

function OrganizationDetailPage() {
  const { organizationId } = Route.useParams()
  const queryClient = useQueryClient()
  const featureSort = useTableSort<FeatureSortField>('feature')
  const userSort = useTableSort<OrgUserSortField>('name')
  const [deprecatedOpen, setDeprecatedOpen] = useState(false)

  const { data: orgData, isLoading: orgLoading } = useQuery(adminGetOrganization, { organizationId })
  const { data: statsData, isLoading: statsLoading } = useQuery(adminGetOrganizationStats, { organizationId })
  const { data: catalogData, isLoading: catalogLoading, isError: catalogError } = useQuery(
    adminListFeatureCatalog,
    {},
  )

  const invalidateOrg = () => {
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: adminGetOrganization,
        cardinality: undefined,
      }),
    })
  }

  const enableMutation = useMutation(adminEnableFeature, {
    onSuccess: () => {
      toast.success('Feature enabled')
      invalidateOrg()
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to enable feature')
    },
  })

  const disableMutation = useMutation(adminDisableFeature, {
    onSuccess: () => {
      toast.success('Feature disabled')
      invalidateOrg()
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to disable feature')
    },
  })

  const enabledFeatureSet = useMemo(
    () => new Set(orgData?.features?.filter((f) => f.enabled).map((f) => f.feature) ?? []),
    [orgData?.features],
  )

  const featureMap = useMemo(
    () => new Map(orgData?.features?.map((f) => [f.feature, f]) ?? []),
    [orgData?.features],
  )

  const users = orgData?.users ?? []

  const { liveFeatures, deprecatedFeatures } = useMemo(
    () => splitFeatureCatalog(catalogData?.entries ?? [], enabledFeatureSet),
    [catalogData?.entries, enabledFeatureSet],
  )

  const sortedFeatures = useMemo(() => {
    const dir = featureSort.sortDirection === 'asc' ? 1 : -1
    return [...liveFeatures].sort((a, b) => {
      const af = featureMap.get(a)
      const bf = featureMap.get(b)
      switch (featureSort.sortField) {
        case 'feature':
          return compareStrings(featureLabel(a), featureLabel(b)) * dir
        case 'status': {
          const aOn = enabledFeatureSet.has(a) ? 1 : 0
          const bOn = enabledFeatureSet.has(b) ? 1 : 0
          return (aOn - bOn) * dir
        }
        case 'expires':
          return compareNumbers(
            af?.expiresAt?.seconds ?? 0n,
            bf?.expiresAt?.seconds ?? 0n,
          ) * dir
        default:
          return 0
      }
    })
  }, [liveFeatures, featureSort.sortField, featureSort.sortDirection, featureMap, enabledFeatureSet])

  const sortedUsers = useMemo(() => {
    const dir = userSort.sortDirection === 'asc' ? 1 : -1
    return [...users].sort((a, b) => {
      switch (userSort.sortField) {
        case 'name': {
          const aName = [a.firstName, a.lastName].filter(Boolean).join(' ')
          const bName = [b.firstName, b.lastName].filter(Boolean).join(' ')
          return compareStrings(aName, bName) * dir
        }
        case 'email':
          return compareStrings(a.email, b.email) * dir
        case 'title':
          return compareStrings(a.title, b.title) * dir
        case 'joined':
          return compareNumbers(
            a.createdAt?.seconds ?? 0n,
            b.createdAt?.seconds ?? 0n,
          ) * dir
        default:
          return 0
      }
    })
  }, [users, userSort.sortField, userSort.sortDirection])

  if (orgLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  const org = orgData?.organization
  const stats = statsData?.stats

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-3">
        <Link
          to="/organizations"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Organizations
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="min-w-0 text-2xl font-bold">{org?.name ?? 'Organization'}</h1>
          {org?.slug && (
            <Badge variant="secondary">{org.slug}</Badge>
          )}
        </div>
        {org?.createdAt && (
          <p className="text-sm text-muted-foreground">
            Created {new Date(Number(org.createdAt.seconds) * 1000).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        <StatCard
          icon={<Users className="h-4 w-4 text-muted-foreground" />}
          label="Users"
          value={statsLoading ? '--' : String(stats?.userCount ?? 0)}
        />
        <StatCard
          icon={<FileText className="h-4 w-4 text-muted-foreground" />}
          label="Leads"
          value={statsLoading ? '--' : String(stats?.leadCount ?? 0)}
        />
        <StatCard
          icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
          label="Events"
          value={statsLoading ? '--' : String(stats?.eventCount ?? 0)}
        />
        <StatCard
          icon={<Receipt className="h-4 w-4 text-muted-foreground" />}
          label="Invoices"
          value={statsLoading ? '--' : String(stats?.invoiceCount ?? 0)}
        />
        <StatCard
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
          label="Revenue"
          value={
            statsLoading
              ? '--'
              : `$${(Number(stats?.revenueCents ?? 0n) / 100).toLocaleString()}`
          }
        />
      </div>

      <SupportAccessSection organizationId={organizationId} />

      {/* Features Section */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Features</h2>
          <AddFeatureDialog
            organizationId={organizationId}
            liveFeatures={liveFeatures}
            enabledFeatures={enabledFeatureSet}
            onSuccess={invalidateOrg}
          />
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <SortableHeader
                    active={featureSort.sortField === 'feature'}
                    direction={featureSort.sortDirection}
                    onToggle={() => featureSort.toggleSort('feature')}
                  >
                    Feature
                  </SortableHeader>
                </TableHead>
                <TableHead>
                  <SortableHeader
                    active={featureSort.sortField === 'status'}
                    direction={featureSort.sortDirection}
                    onToggle={() => featureSort.toggleSort('status')}
                  >
                    Status
                  </SortableHeader>
                </TableHead>
                <TableHead>
                  <SortableHeader
                    active={featureSort.sortField === 'expires'}
                    direction={featureSort.sortDirection}
                    onToggle={() => featureSort.toggleSort('expires')}
                  >
                    Expires
                  </SortableHeader>
                </TableHead>
                <TableHead className="w-20 text-right">Toggle</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {catalogLoading || catalogError ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    {catalogError ? 'Failed to load feature catalog' : 'Loading...'}
                  </TableCell>
                </TableRow>
              ) : (
                sortedFeatures.map((value) => {
                  const feature = featureMap.get(value)
                  const enabled = enabledFeatureSet.has(value)

                  return (
                    <TableRow key={value}>
                      <TableCell className="font-medium">{featureLabel(value)}</TableCell>
                      <TableCell>
                        <Badge variant={enabled ? 'success' : 'secondary'}>
                          {enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {feature?.expiresAt
                          ? new Date(Number(feature.expiresAt.seconds) * 1000).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Switch
                          checked={enabled}
                          disabled={enableMutation.isPending || disableMutation.isPending}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              enableMutation.mutate({
                                organizationId,
                                feature: value,
                              })
                            } else {
                              disableMutation.mutate({
                                organizationId,
                                feature: value,
                              })
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Deprecated Features Section */}
      {deprecatedFeatures.length > 0 && (
        <div className="space-y-4">
          <button
            type="button"
            className="flex items-center gap-1 text-lg font-semibold text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setDeprecatedOpen((open) => !open)}
          >
            {deprecatedOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Deprecated ({deprecatedFeatures.length})
          </button>
          {deprecatedOpen && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feature</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-20 text-right">Disable</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deprecatedFeatures.map((value) => (
                    <TableRow key={value}>
                      <TableCell className="font-medium">{featureLabel(value)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">Dead flag - disable only</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Switch
                          checked
                          disabled={disableMutation.isPending}
                          onCheckedChange={(checked) => {
                            if (!checked) {
                              disableMutation.mutate({
                                organizationId,
                                feature: value,
                              })
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Users Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Users</h2>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <SortableHeader
                    active={userSort.sortField === 'name'}
                    direction={userSort.sortDirection}
                    onToggle={() => userSort.toggleSort('name')}
                  >
                    Name
                  </SortableHeader>
                </TableHead>
                <TableHead>
                  <SortableHeader
                    active={userSort.sortField === 'email'}
                    direction={userSort.sortDirection}
                    onToggle={() => userSort.toggleSort('email')}
                  >
                    Email
                  </SortableHeader>
                </TableHead>
                <TableHead>
                  <SortableHeader
                    active={userSort.sortField === 'title'}
                    direction={userSort.sortDirection}
                    onToggle={() => userSort.toggleSort('title')}
                  >
                    Title
                  </SortableHeader>
                </TableHead>
                <TableHead>
                  <SortableHeader
                    active={userSort.sortField === 'joined'}
                    direction={userSort.sortDirection}
                    onToggle={() => userSort.toggleSort('joined')}
                  >
                    Joined
                  </SortableHeader>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!sortedUsers.length ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No users found for this organization
                  </TableCell>
                </TableRow>
              ) : (
                sortedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || '-'}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.title || '-'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.createdAt
                        ? new Date(Number(user.createdAt.seconds) * 1000).toLocaleDateString()
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
}

function AddFeatureDialog({
  organizationId,
  liveFeatures,
  enabledFeatures,
  onSuccess,
}: {
  organizationId: string
  liveFeatures: OrganizationFeatureEnum[]
  enabledFeatures: Set<OrganizationFeatureEnum>
  onSuccess: () => void
}) {
  const [open, setOpen] = useState(false)
  const [selectedFeature, setSelectedFeature] = useState<string>('')
  const [expiryDate, setExpiryDate] = useState('')

  const availableFeatures = availableFeaturesFor(liveFeatures, enabledFeatures)

  const enableMutation = useMutation(adminEnableFeature, {
    onSuccess: () => {
      toast.success('Feature enabled')
      setOpen(false)
      setSelectedFeature('')
      setExpiryDate('')
      onSuccess()
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to enable feature')
    },
  })

  const handleConfirm = () => {
    if (!selectedFeature) return

    const feature = Number(selectedFeature) as OrganizationFeatureEnum

    let expiresAt = undefined
    if (expiryDate) {
      const date = new Date(expiryDate)
      if (!isNaN(date.getTime())) {
        expiresAt = create(TimestampSchema, {
          seconds: BigInt(Math.floor(date.getTime() / 1000)),
          nanos: 0,
        })
      }
    }

    enableMutation.mutate({
      organizationId,
      feature,
      expiresAt,
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Feature
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Feature</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-2">
            <Label>Feature</Label>
            <Select value={selectedFeature} onValueChange={setSelectedFeature}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a feature" />
              </SelectTrigger>
              <SelectContent>
                {availableFeatures.map((f) => (
                  <SelectItem key={f} value={String(f)}>
                    {featureLabel(f)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Expiry Date (optional)</Label>
            <input
              type="date"
              className="border-input flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedFeature || enableMutation.isPending}
            >
              {enableMutation.isPending ? 'Enabling...' : 'Enable Feature'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
